import { z } from 'zod';
/**
 * Контракты модуля `modules/telegram` — бот, создающий записи из рабочих
 * чатов Telegram. См. `docs/modules/telegram-bot.md`.
 *
 * Номера чатов, сообщений и аккаунтов Telegram — строки, а не числа:
 * они проходят через JSON, а номера чатов не помещаются в безопасный
 * диапазон целых чисел JavaScript. Арифметики над ними нет.
 */
const TelegramId = z.string().regex(/^-?\d{1,20}$/, { message: 'Некорректный номер Telegram' });
// ─── Сопоставление полей ─────────────────────────────────────────────────
//
// Что из сообщения в какое поле типа записи. null — «не переносить».
// `attachments` — не поле, а флаг: класть ли файлы сообщения во вложения.
export const TelegramFieldMappingSchema = z.object({
    text: z.string().uuid().nullish(),
    title: z.string().uuid().nullish(),
    author: z.string().uuid().nullish(),
    link: z.string().uuid().nullish(),
    date: z.string().uuid().nullish(),
    attachments: z.boolean().default(false),
});
// ─── Триггеры ────────────────────────────────────────────────────────────
//
// Чем в чате запускается создание записи. Хотя бы одно должно быть задано —
// проверяется сервисом при сохранении привязки.
export const TelegramTriggersSchema = z.object({
    // Текстовая команда, например `/task`. Регистр не важен.
    //
    // Других способов создать запись нет намеренно: упоминание бота Telegram
    // доставляет только с отключённым режимом приватности (тогда бот видит всю
    // переписку), а чужие реакции — только администраторам чата. Ни того, ни
    // другого рабочим чатам не нужно.
    command: z.string().trim().min(2).max(32).nullish(),
});
export const TelegramWhoCanCreateSchema = z.enum(['all', 'linked']);
// ─── Привязка чата ───────────────────────────────────────────────────────
export const TelegramChatBindingSchema = z.object({
    id: z.string().uuid(),
    spaceId: z.string().uuid(),
    chatId: TelegramId,
    chatTitle: z.string().nullable(),
    chatUrl: z.string().nullable(),
    itemTypeId: z.string().uuid().nullable(),
    viewId: z.string().uuid().nullable(),
    fieldMapping: TelegramFieldMappingSchema,
    // Фиксированные значения полей: id поля → значение. Именно id, а не slug:
    // slug у поля можно переименовать, и настройка чата тогда молча сломалась бы.
    defaults: z.record(z.unknown()),
    triggers: TelegramTriggersSchema,
    whoCanCreate: TelegramWhoCanCreateSchema,
    threadSync: z.boolean(),
    // Сообщать в чат о новых записях, заведённых в интерфейсе Lens.
    // Область — всё пространство: тип записи и представление привязки
    // задают, куда попадают записи ИЗ чата, а не о чём рассказывать.
    announceNew: z.boolean(),
    enabled: z.boolean(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const TelegramBindingListResponseSchema = z.object({
    bindings: z.array(TelegramChatBindingSchema),
});
// ─── Настройка привязки ──────────────────────────────────────────────────
//
// Куда создавать — либо тип записи, либо представление (тогда тип берётся
// из него, а `=`-фильтры доподставляют значения полей, FR-4.1). Ровно одно
// из двух — проверяется сервисом.
export const UpdateTelegramBindingRequestSchema = z.object({
    itemTypeId: z.string().uuid().nullish(),
    viewId: z.string().uuid().nullish(),
    chatUrl: z.string().trim().max(500).nullish(),
    fieldMapping: TelegramFieldMappingSchema.partial().optional(),
    defaults: z.record(z.unknown()).optional(),
    triggers: TelegramTriggersSchema.partial().optional(),
    whoCanCreate: TelegramWhoCanCreateSchema.optional(),
    threadSync: z.boolean().optional(),
    announceNew: z.boolean().optional(),
    enabled: z.boolean().optional(),
});
// ─── Код подключения чата ────────────────────────────────────────────────
//
// Выдаётся в настройках пространства, гасится командой `/привязать КОД`
// в чате. Живёт 15 минут.
export const TelegramConnectCodeResponseSchema = z.object({
    code: z.string(),
    expiresAt: z.string(),
    botUsername: z.string().nullable(),
});
// ─── Создание недостающих полей ──────────────────────────────────────────
//
// Кнопка «Создать недостающие поля» на экране сопоставления: заводит в типе
// записи служебные поля и возвращает обновлённое сопоставление.
export const TelegramCreateFieldsRequestSchema = z.object({
    itemTypeId: z.string().uuid(),
    fields: z.array(z.enum(['title', 'text', 'author', 'link', 'date', 'source'])).min(1),
});
// ─── Привязка аккаунта Telegram к пользователю Lens ──────────────────────
//
// Данные приходят от Telegram (кнопка `login_url` или виджет входа) уже
// подписанными: `hash` — HMAC-SHA256 по остальным полям на ключе, который
// сам является SHA-256 от токена бота. Проверяется на бэкенде.
export const TelegramLoginPayloadSchema = z.object({
    id: TelegramId,
    first_name: z.string().nullish(),
    last_name: z.string().nullish(),
    username: z.string().nullish(),
    photo_url: z.string().nullish(),
    auth_date: z.string(),
    hash: z.string(),
});
export const TelegramAccountSchema = z.object({
    telegramUserId: TelegramId,
    username: z.string().nullable(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    userId: z.string().uuid().nullable(),
    userDisplayName: z.string().nullable(),
    linkedAt: z.string().nullable(),
    lastSeenAt: z.string().nullable(),
});
// Ответ страницы `/telegram/link`: что за аккаунт и с чем его свяжут.
export const TelegramLinkResultSchema = z.object({
    status: z.enum(['linked', 'already_linked_other_user', 'replaced']),
    account: TelegramAccountSchema,
    // Сколько прежних записей этого человека получили автора задним числом.
    backfilledItems: z.number().int().nonnegative(),
});
// ─── Экран «Аккаунты Telegram» ───────────────────────────────────────────
//
// Замеченные в чатах аккаунты рядом с подобранными пользователями Lens.
// `matchedBy` говорит, почему подобралась пара: совпало имя или username.
export const TelegramAccountSuggestionSchema = z.object({
    account: TelegramAccountSchema,
    suggestedUserId: z.string().uuid().nullable(),
    suggestedUserDisplayName: z.string().nullable(),
    matchedBy: z.enum(['display_name', 'username', 'none']),
    ambiguous: z.boolean(),
});
export const TelegramAccountsResponseSchema = z.object({
    accounts: z.array(TelegramAccountSuggestionSchema),
});
export const TelegramLinkAccountsRequestSchema = z.object({
    links: z
        .array(z.object({ telegramUserId: TelegramId, userId: z.string().uuid() }))
        .min(1)
        .max(500),
});
// ─── Контракт «бот → api» ────────────────────────────────────────────────
//
// Сервис бота ходит в api по общему секрету (заголовок
// `X-Telegram-Bot-Secret`), а не по токену пользователя: автор записи
// определяется на бэкенде по связке телеграм-аккаунта, поэтому
// имперсонация владельцем токена здесь не годится.
export const TelegramBotMessageSchema = z.object({
    chatId: TelegramId,
    chatTitle: z.string().nullish(),
    messageId: TelegramId,
    // Номер сообщения, на которое ответили командой. Если команду написали
    // без ответа — совпадает с messageId.
    sourceMessageId: TelegramId,
    text: z.string(),
    // Кто нажал команду или поставил реакцию.
    from: z.object({
        id: TelegramId,
        username: z.string().nullish(),
        firstName: z.string().nullish(),
        lastName: z.string().nullish(),
    }),
    // Автор исходного сообщения, если команду написали ответом на чужое.
    sourceFrom: z
        .object({
        id: TelegramId,
        username: z.string().nullish(),
        firstName: z.string().nullish(),
        lastName: z.string().nullish(),
    })
        .nullish(),
    date: z.string(),
    // Чем запустили. Пока способ один — команда, но поле оставлено объектом:
    // по нему выбирается привязка, и добавить второй способ проще, чем
    // менять контракт.
    trigger: z.object({
        kind: z.literal('command'),
        value: z.string().nullish(),
    }),
    messageUrl: z.string().nullish(),
});
export const TelegramBotCreateResultSchema = z.object({
    status: z.enum(['created', 'duplicate', 'no_binding', 'forbidden', 'error']),
    itemId: z.string().uuid().nullable(),
    itemUrl: z.string().nullable(),
    displayKey: z.string().nullable(),
    // Текст для ответа в чат — готовый, боту не надо ничего собирать.
    message: z.string(),
    // Нужно ли предложить человеку связать аккаунт.
    suggestLink: z.boolean(),
});
// Команда `/привязать КОД` из чата.
export const TelegramBotConnectRequestSchema = z.object({
    code: z.string().trim().min(4).max(32),
    chatId: TelegramId,
    chatTitle: z.string().nullish(),
    from: z.object({ id: TelegramId, username: z.string().nullish() }),
});
export const TelegramBotConnectResultSchema = z.object({
    status: z.enum(['connected', 'invalid_code', 'expired', 'already_used']),
    spaceName: z.string().nullable(),
    message: z.string(),
});
// Отметка «этот человек писал в подключённом чате» — наполняет список
// аккаунтов для массового подбора пар.
export const TelegramBotSeenRequestSchema = z.object({
    accounts: z
        .array(z.object({
        id: TelegramId,
        username: z.string().nullish(),
        firstName: z.string().nullish(),
        lastName: z.string().nullish(),
    }))
        .min(1)
        .max(200),
});
// ─── Очередь «Lens → чат» ────────────────────────────────────────────────
//
// Бот опрашивает api и отправляет готовые тексты. Текст собирает api: боту
// не надо знать ни полей записи, ни адреса стенда.
export const TelegramOutboxMessageSchema = z.object({
    id: z.string().uuid(),
    chatId: TelegramId,
    text: z.string(),
});
export const TelegramOutboxResponseSchema = z.object({
    messages: z.array(TelegramOutboxMessageSchema),
});
// Отчёт бота о доставке. Неудача возвращает строку в очередь до исчерпания
// попыток, чтобы упавший на минуту Telegram не съедал сообщения молча.
export const TelegramOutboxAckRequestSchema = z.object({
    id: z.string().uuid(),
    status: z.enum(['sent', 'failed']),
    error: z.string().max(500).nullish(),
});
// ─── Личные уведомления в Telegram ──────────────────────────────────────
//
// Упоминание в комментарии и назначение в поле дублируются человеку в
// переписку с ботом (docs/modules/telegram-bot.md §9). Включаются в
// пространстве админом; каждый человек может выключить их совсем или в
// отдельных пространствах.
// Личная настройка (хранится в user_preferences под ключом telegramNotifications).
// Нет записи — уведомления включены во всех пространствах.
export const TelegramNotifyPreferenceSchema = z.object({
    enabled: z.boolean(),
    mutedSpaceIds: z.array(z.string().uuid()).max(500),
});
export const TelegramNotifySpaceSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    slug: z.string(),
    icon: z.string().nullable(),
    // Включены ли уведомления в самом пространстве (решает его админ).
    spaceEnabled: z.boolean(),
    // Человек выключил это пространство у себя.
    muted: z.boolean(),
});
// GET /api/telegram/me/notifications
export const TelegramNotifySettingsResponseSchema = z.object({
    enabled: z.boolean(),
    linked: z.boolean(),
    // Человек нажал «Start» у бота: без этого бот не может написать первым.
    botChatOpened: z.boolean(),
    spaces: z.array(TelegramNotifySpaceSchema),
});
// GET/PUT /api/spaces/:id/telegram/notifications
export const TelegramSpaceNotifySettingsSchema = z.object({
    enabled: z.boolean(),
});
